import { updateRecord } from '@ms/yammer-data-support';

import { Publisher, PublisherAttachmentReference, Publishers } from '../types';

interface AddAttachmentToPublisherInput {
  readonly state: Publishers;
  readonly publisherId: Publisher['id'];
  readonly attachmentToAdd: PublisherAttachmentReference;
  readonly isHeaderImage?: boolean;
}

type AddAttachmentToPublisher = (input: AddAttachmentToPublisherInput) => Publishers;

type ComparablePublisherAttachmentProps = Pick<PublisherAttachmentReference, 'id' | 'schema'>;

const areAttachmentsEqual = (
  attachmentA: ComparablePublisherAttachmentProps,
  attachmentB: ComparablePublisherAttachmentProps
) => attachmentA.schema === attachmentB.schema && attachmentA.id === attachmentB.id;

export const addAttachmentToPublisher: AddAttachmentToPublisher = ({
  state,
  publisherId,
  attachmentToAdd,
  isHeaderImage,
}) => {
  const publisher = state[publisherId];

  if (!publisher) {
    return state;
  }

  const attachments = publisher.attachments;
  const referenceIsAlreadyAttached =
    attachments.findIndex((attachment) => areAttachmentsEqual(attachment, attachmentToAdd)) >= 0;

  if (referenceIsAlreadyAttached) {
    return state;
  }

  if (isHeaderImage) {
    const attachmentsWithoutHeaderImage = attachments.filter((attachment) => attachment.id !== attachmentToAdd.id);

    return updateRecord(state, publisherId, {
      headerImage: attachmentToAdd,
      attachments: [...attachmentsWithoutHeaderImage],
      removeHeaderImage: undefined,
    });
  }

  return updateRecord(state, publisherId, { attachments: [...attachments, attachmentToAdd] });
};

interface RemoveAttachmentsFromPublisherInput {
  readonly state: Publishers;
  readonly publisherId: Publisher['id'];
  readonly attachmentsToRemove: ComparablePublisherAttachmentProps[];
}

const attachmentsToRemoveIncludesHeaderImage = (
  headerImage: Publisher['headerImage'],
  attachmentsToRemove: ComparablePublisherAttachmentProps[]
) => attachmentsToRemove.some((attachmentToRemove) => attachmentToRemove.id === headerImage?.id);

const attachmentToRemoveContainsAttachment = (
  attachment: ComparablePublisherAttachmentProps,
  attachmentsToRemove: ComparablePublisherAttachmentProps[]
) => attachmentsToRemove.some((attachmentToRemove) => areAttachmentsEqual(attachment, attachmentToRemove));

type RemoveAttachmentsFromPublisher = (input: RemoveAttachmentsFromPublisherInput) => Publishers;

export const removeAttachmentsFromPublisher: RemoveAttachmentsFromPublisher = ({
  state,
  publisherId,
  attachmentsToRemove,
}) => {
  const publisher = state[publisherId];

  if (!publisher) {
    return state;
  }

  const attachments = publisher.attachments;
  const withoutAttachment = attachments.filter(
    (attachment) => !attachmentToRemoveContainsAttachment(attachment, attachmentsToRemove)
  );

  if (attachmentsToRemoveIncludesHeaderImage(publisher.headerImage, attachmentsToRemove)) {
    return updateRecord(state, publisherId, {
      attachments: withoutAttachment,
      headerImage: undefined,
      removeHeaderImage: true,
    });
  }

  if (attachments.length === withoutAttachment.length) {
    return state;
  }

  return updateRecord(state, publisherId, {
    attachments: withoutAttachment,
  });
};

interface UpdateInlineAttachmentIdsInput {
  readonly state: Publishers;
  readonly publisherId: Publisher['id'];
  readonly inlineAttachmentIds: string[];
}

export const updateInlineAttachmentIds = (input: UpdateInlineAttachmentIdsInput): Publishers => {
  const { state, publisherId, inlineAttachmentIds } = input;
  const publisher = state[publisherId];

  if (!publisher) {
    return state;
  }

  return updateRecord(state, publisherId, {
    inlineAttachmentIds,
  });
};

interface UpdateAttachmentReferenceInput {
  readonly state: Publishers;
  readonly publisherId: Publisher['id'];
  readonly attachmentToUpdate: PublisherAttachmentReference;
  readonly updatedAttachment: PublisherAttachmentReference;
  readonly isHeaderImage?: boolean;
}

type UpdateAttachmentReference = (input: UpdateAttachmentReferenceInput) => Publishers;

export const updateAttachmentReference: UpdateAttachmentReference = ({
  state,
  publisherId,
  attachmentToUpdate,
  updatedAttachment,
  isHeaderImage,
}) => {
  const publisher = state[publisherId];

  if (!publisher) {
    return state;
  }

  const attachments = publisher.attachments;
  const updateIndex = attachments.findIndex((attachment) => areAttachmentsEqual(attachment, attachmentToUpdate));

  if (isHeaderImage) {
    const attachmentsWithoutHeaderImage = attachments.filter((attachment) => attachment.id !== attachmentToUpdate.id);

    return updateRecord(state, publisherId, {
      headerImage: updatedAttachment,
      removeHeaderImage: undefined,
      attachments: attachmentsWithoutHeaderImage,
    });
  }

  if (updateIndex === -1) {
    return state;
  }

  const newAttachments = [
    ...attachments.slice(0, updateIndex),
    updatedAttachment,
    ...attachments.slice(updateIndex + 1),
  ];

  return updateRecord(state, publisherId, {
    attachments: newAttachments,
  });
};
